import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type BadgeTone =
  'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'accent'

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-tint text-ink-soft',
  brand: 'bg-brand-tint text-brand-on-tint',
  success: 'bg-success-tint text-success',
  warning: 'bg-warning-tint text-warning',
  danger: 'bg-danger-tint text-danger',
  accent: 'bg-accent-tint text-accent-strong',
}

interface BadgeProps {
  tone?: BadgeTone
  /* Leading status dot — reads at a glance before the label does. */
  dot?: boolean
  className?: string
  children: ReactNode
}

export function Badge({
  tone = 'neutral',
  dot = false,
  className,
  children,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-current"
        />
      )}
      {children}
    </span>
  )
}

/* Domain status → tone + human label. Statuses arrive as backend enums. */
const STATUS_STYLES: Record<string, { tone: BadgeTone; label: string }> = {
  // Orders / payments
  pending: { tone: 'warning', label: 'Pending' },
  pending_payment: { tone: 'warning', label: 'Payment pending' },
  awaiting_payment: { tone: 'warning', label: 'Payment pending' },
  verification_pending: { tone: 'warning', label: 'Being verified' },
  paid: { tone: 'success', label: 'Paid' },
  captured: { tone: 'success', label: 'Paid' },
  confirmed: { tone: 'success', label: 'Confirmed' },
  accepted: { tone: 'brand', label: 'Accepted' },
  prepared: { tone: 'brand', label: 'Prepared' },
  dispatched: { tone: 'brand', label: 'On its way' },
  preparing: { tone: 'brand', label: 'Preparing' },
  out_for_delivery: { tone: 'brand', label: 'On its way' },
  delivered: { tone: 'success', label: 'Delivered' },
  completed: { tone: 'success', label: 'Completed' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
  refunded: { tone: 'neutral', label: 'Refunded' },
  failed: { tone: 'danger', label: 'Failed' },
  cash_on_delivery: { tone: 'accent', label: 'Pay on delivery' },
  // Subscriptions / fulfilments
  active: { tone: 'success', label: 'Active' },
  scheduled: { tone: 'brand', label: 'Scheduled' },
  awaiting_menu: { tone: 'warning', label: 'Awaiting menu' },
  capacity_exception: { tone: 'warning', label: 'Needs attention' },
  held: { tone: 'accent', label: 'On hold' },
  on_hold: { tone: 'accent', label: 'On hold' },
  paused: { tone: 'accent', label: 'Paused' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  expired: { tone: 'neutral', label: 'Ended' },
  requested: { tone: 'warning', label: 'Requested' },
  approved: { tone: 'success', label: 'Approved' },
  rejected: { tone: 'danger', label: 'Declined' },
}

export function statusStyle(status: string): {
  tone: BadgeTone
  label: string
} {
  return (
    STATUS_STYLES[status] ?? {
      tone: 'neutral',
      label: status.replaceAll('_', ' '),
    }
  )
}

export function StatusBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const { tone, label } = statusStyle(status)
  return (
    <Badge tone={tone} dot className={className}>
      {label}
    </Badge>
  )
}
